import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import type { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { useTranslation } from 'app/lang';
import { replacingHyphenOnDash } from 'app/lib/replaceDashSymbol';
import Dropdown, { type ChangeHandler } from 'app/widgets/Dropdown/Single';

import type { SetPledgeStake } from '../../../../../actions/paymentOptions';
import { setPledgeStake } from '../../../../../actions/paymentOptions';
import { SubmissionState } from '../../../../../constants';
import { PledgeStakesContext } from '../../../../../contexts/pledgeStakesSelect';
import {
  selectAllUsedPledgeStakes,
  selectAnyHasLockedState,
  selectSomeHasError,
} from '../../../../../selectors/entries';
import { selectSubmission } from '../../../../../selectors/submission';
import type { PledgeStakeDAO } from '../../../../../types/backend/paymentOptions';
import { isChainPledgeStake } from '../../../../../types/backend/paymentOptions';
import DropdownComponents, { Tab } from './DropdownComponents';

import dictionary from './PledgeStakeDropdown.dictionary.json';

import styles from './PledgeStakeDropdown.module.scss';

// ---

const MAX_MENU_HEIGHT = 250;

// ---

type StoreProps = {
  disabled: boolean;
  usedPledgeStakes: Set<number>;
};

type DispatchProp = {
  dispatch: ThunkDispatchType<SetPledgeStake>;
};

type PledgeStakeDropdownProps = {
  betId?: number;
  className?: string;
  minValue?: number;
  activePledgeStake?: PledgeStakeDAO | null;
};

const PledgeStakeDropdown: FC<PledgeStakeDropdownProps & DispatchProp & StoreProps> = ({
  betId,
  className,
  activePledgeStake,
  minValue = 0,
  disabled,
  usedPledgeStakes,
  dispatch,
}) => {
  const { t } = useTranslation();
  const { canMakeRequest, pledgeStakes, fetchPledgeStakes } = useContext(PledgeStakesContext);
  const [menuPlacement, setMenuPlacement] = useState<'auto' | 'top' | 'bottom'>('auto');
  const [menuListBottom, setMenuListBottom] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>(Tab.SINGLE);

  const getOptionLabelHandler = useCallback((option: PledgeStakeDAO) => {
    if (isChainPledgeStake(option)) {
      return t(dictionary.optionLabel, { length: option.chainMatches.length });
    }

    return replacingHyphenOnDash(option.matchName);
  }, []);

  const getOptionValueHandler = useCallback((option: PledgeStakeDAO) => {
    return option.stakeId.toString();
  }, []);

  const onMenuOpenHandler = useCallback(() => {
    canMakeRequest && fetchPledgeStakes(betId);

    if (menuListBottom && menuListBottom < window.innerHeight && menuPlacement === 'top') {
      setMenuPlacement('auto');
    }
  }, [canMakeRequest, betId, menuListBottom, menuPlacement]);

  const selectPledgeStakeHandler: ChangeHandler<PledgeStakeDAO> = useCallback((option) => {
    if (!option || !('stakeId' in option)) {
      console.error('selectPledgeStakeHandler -> `option` not found', option);
      return;
    }

    if (!betId) {
      console.error(`selectPledgeStakeHandler -> betId not found = ${betId}`);
      return;
    }

    dispatch(setPledgeStake(betId, option));
  }, [betId]);

  const noOptionsMessage = useCallback(() => t(dictionary.noOptionsMessage), []);

  const isOptionDisabledHandler = useCallback((option: PledgeStakeDAO) => {
    return usedPledgeStakes.has(option.stakeId) || option.maxWinning < minValue;
  }, [usedPledgeStakes, minValue]);

  let options: PledgeStakeDAO[] = [];

  switch (activeTab) {
    case Tab.SINGLE:
      options = pledgeStakes.items.singleStakes; break;
    case Tab.CHAIN:
      options = pledgeStakes.items.chainStakes; break;
  }

  const menuPortalTarget: HTMLElement | null = document.querySelector('[data-id=sidebar]');

  const components = DropdownComponents(
    pledgeStakes.loading,
    activeTab,
    setActiveTab,
    minValue,
    usedPledgeStakes,
    setMenuListBottom,
    menuPlacement,
    menuListBottom,
    betId,
  );

  useEffect(() => {
    if (menuListBottom && menuListBottom > window.innerHeight) {
      setMenuPlacement('top');
    }
  }, [menuListBottom]);

  return (
    <Dropdown<PledgeStakeDAO>
      closeMenuOnScroll
      isSearchable={false}
      menuPortalTarget={menuPortalTarget}
      menuPlacement={menuPlacement}
      placeholder={t(dictionary.placeholder)}
      options={options}
      noOptionsMessage={noOptionsMessage}
      isLoading={pledgeStakes.loading}
      loadingMessage={() => null}
      className={classNames(styles.dropdown, className, { [styles.loading]: pledgeStakes.loading })}
      value={activePledgeStake}
      menuPosition="fixed"
      disabled={disabled}
      indicatorSize={20}
      maxMenuHeight={MAX_MENU_HEIGHT}
      onMenuOpen={onMenuOpenHandler}
      onChange={selectPledgeStakeHandler}
      getOptionValue={getOptionValueHandler}
      getOptionLabel={getOptionLabelHandler}
      isOptionDisabled={isOptionDisabledHandler}
      components={components} />
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => {
  const hasEntriesErrors = selectSomeHasError(state) || selectAnyHasLockedState(state);

  return {
    disabled: selectSubmission(state) !== SubmissionState.AWAITING || hasEntriesErrors,
    usedPledgeStakes: selectAllUsedPledgeStakes(state),
  };
};

export default connect(mapStateToProps)(PledgeStakeDropdown);
